import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import CustomSelect from "../components/common/CustomSelect";
import { Loading } from "../components/common/Loading";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";

const CompletedComplaints = () => {
    const { user, loading: userLoading, users, usersLoading } = useAuth();

    const [complaints, setComplaints] = useState([]);
    const [filteredComplaints, setFilteredComplaints] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filterStatus, setFilterStatus] = useState("all");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null); // For mobile view

    const itemsPerPage = 10;

    const statusOptions = ["completed", "paused", "in-operation"];
    const categoryOptions = [
        "Red signal",
        "Router setup",
        "Billing issue",
        "New connection setup",
        "New connection survey",
        "CCTV survey",
        "CCTV setup",
        "Ecommerce",
        "Other",
    ];

    const technicians = useMemo(() => {
        return users.filter((u) => u.role === "technician");
    }, [users]);

    // 🔹 Fetch data
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "complaints"),
            (snapshot) => {
                let data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Only completed & paused
                data = data.filter(
                    (c) => c.status === "completed" || c.status === "paused"
                );

                // Role-based filtering
                if (user?.role === "technician") {
                    data = data.filter((c) => c.assignedTo === user.uid);
                }

                // Sort
                data.sort(
                    (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
                );

                setComplaints(data);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load completed complaints");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // 🔹 Combined filtering (status + category)
    useEffect(() => {
        let filtered = [...complaints];

        // Status filter
        if (filterStatus !== "all") {
            filtered = filtered.filter((c) => c.status === filterStatus);
        }

        // Category filter
        if (selectedCategory) {
            filtered = filtered.filter(
                (c) => c.category === selectedCategory
            );
        }

        setFilteredComplaints(filtered);
        setCurrentPage(1);
    }, [complaints, filterStatus, selectedCategory]);

    // 🔹 Status update
    const handleStatusChange = async (complaintId, newStatus) => {
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);

            await updateDoc(complaintRef, {
                status: newStatus,
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message: `Status changed to ${newStatus}`,
                        userId: user.uid,
                    },
                ],
            });

            toast.success(`Status updated to ${newStatus}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        }
    };

    // Format date helper
    const formatDate = (timestamp) => {
        if (!timestamp) return "—";
        const date = new Date(timestamp.seconds * 1000);
        return new Intl.DateTimeFormat('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    // Status badge component
    const StatusBadge = ({ status }) => {
        const statusColors = {
            'completed': 'bg-green-100 text-green-800 border-green-200',
            'paused': 'bg-yellow-100 text-yellow-800 border-yellow-200',
            'in-operation': 'bg-blue-100 text-blue-800 border-blue-200'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                {status}
            </span>
        );
    };

    if (loading || userLoading || usersLoading) return <Loading />;

    // 🔹 Empty state with clear filters
    if (!filteredComplaints.length) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4">
                <div className="text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h2 className="text-xl font-semibold text-gray-700">
                        No complaints found
                    </h2>
                    <p className="text-gray-500 mt-2">
                        {filterStatus !== "all" || selectedCategory
                            ? "Try clearing your filters"
                            : "No completed or paused complaints yet"}
                    </p>
                </div>

                {(filterStatus !== "all" || selectedCategory) && (
                    <button
                        onClick={() => {
                            setFilterStatus("all");
                            setSelectedCategory("");
                        }}
                        className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition"
                    >
                        Clear Filters
                    </button>
                )}
            </div>
        );
    }

    // 🔹 Pagination
    const totalPages = Math.ceil(
        filteredComplaints.length / itemsPerPage
    );

    const paginated = filteredComplaints.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
            {/* 🔹 Header */}
            <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
                <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                        Completed & Paused Complaints
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Total: {filteredComplaints.length} complaints
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center w-full sm:w-auto">
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Status Filter */}
                        <div className="w-full sm:w-40">
                            <CustomSelect
                                name="filterStatus"
                                options={["all", "completed", "paused"]}
                                value={filterStatus}
                                onChange={(name, value) =>
                                    setFilterStatus(value)
                                }
                                placeholder="Filter by status"
                            />
                        </div>

                        {/* Category Filter */}
                        <div className="w-full sm:w-48">
                            <CustomSelect
                                name="category"
                                options={categoryOptions}
                                value={selectedCategory}
                                placeholder="Filter by category"
                                onChange={(name, value) =>
                                    setSelectedCategory(value)
                                }
                            />
                        </div>

                        {/* Clear Button */}
                        {(filterStatus !== "all" || selectedCategory) && (
                            <button
                                onClick={() => {
                                    setFilterStatus("all");
                                    setSelectedCategory("");
                                }}
                                className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition whitespace-nowrap"
                                title="Clear filters"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* 🔹 Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full border-collapse bg-white text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Ticket</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Customer</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Phone</th>
                            {/* <th className="px-4 py-3 text-left font-semibold text-gray-600">Department</th> */}
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Assigned To</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Category</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Resolved At</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-600">Comment</th>
                            {user?.role === "admin" && (
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Action</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {paginated.map((c) => (
                            <tr key={c.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-3 font-mono text-xs">
                                    {c.ticketNumber}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-medium">{c.customerInfo?.name}</div>
                                </td>
                                <td className="px-4 py-3">
                                    {c.customerInfo?.phone}
                                </td>
                                {/* <td className="px-4 py-3">
                                    <span className="capitalize">{c.department || "-"}</span>
                                </td> */}
                                <td className="px-4 py-3">
                                    {technicians.find(
                                        (t) => t.uid === c.assignedTo
                                    )?.name ||
                                        <span className="text-gray-400 italic">Unassigned</span>
                                    }
                                </td>
                                <td className="px-4 py-3">
                                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                                        {c.category || "-"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    {formatDate(c.resolvedAt)}
                                </td>
                                <td className="px-4 py-3">
                                    <StatusBadge status={c.status} />
                                </td>
                                <td className="px-4 py-3 max-w-xs">
                                    <div className="truncate" title={c.completionComment}>
                                        {c.completionComment || <span className="text-gray-400">—</span>}
                                    </div>
                                </td>
                                {user?.role === "admin" && (
                                    <td className="px-4 py-3">
                                        <CustomSelect
                                            name="status"
                                            options={statusOptions}
                                            value={c.status}
                                            onChange={(name, value) =>
                                                handleStatusChange(c.id, value)
                                            }
                                            className="text-xs"
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 🔹 Mobile Card View */}
            <div className="lg:hidden space-y-3">
                {paginated.map((c) => (
                    <div key={c.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Card Header - Always Visible */}
                        <div
                            className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                            onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs bg-white px-2 py-1 rounded border border-gray-200">
                                        {c.ticketNumber}
                                    </span>
                                    <StatusBadge status={c.status} />
                                </div>
                                <div className="mt-2">
                                    <div className="font-medium text-gray-800">{c.customerInfo?.name}</div>
                                    <div className="text-sm text-gray-600">{c.customerInfo?.phone}</div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-gray-500">
                                    {formatDate(c.resolvedAt)}
                                </div>
                                <div className="flex items-center justify-end mt-2">
                                    <svg
                                        className={`w-5 h-5 text-gray-400 transition-transform ${expandedRow === c.id ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Expanded Content */}
                        {expandedRow === c.id && (
                            <div className="p-4 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-gray-500 block">Department</label>
                                        <span className="text-sm font-medium capitalize">{c.department || "-"}</span>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 block">Category</label>
                                        <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                                            {c.category || "-"}
                                        </span>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-xs text-gray-500 block">Assigned To</label>
                                        <span className="text-sm">
                                            {technicians.find((t) => t.uid === c.assignedTo)?.name ||
                                                <span className="text-gray-400">Unassigned</span>
                                            }
                                        </span>
                                    </div>
                                    {c.completionComment && (
                                        <div className="col-span-2">
                                            <label className="text-xs text-gray-500 block">Completion Comment</label>
                                            <p className="text-sm bg-gray-50 p-2 rounded mt-1">
                                                {c.completionComment}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {user?.role === "admin" && (
                                    <div className="pt-3 border-t border-gray-200">
                                        <label className="text-xs text-gray-500 block mb-2">Change Status</label>
                                        <CustomSelect
                                            name="status"
                                            options={statusOptions}
                                            value={c.status}
                                            onChange={(name, value) =>
                                                handleStatusChange(c.id, value)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* 🔹 Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 order-2 sm:order-1">
                    Showing <span className="font-medium">{paginated.length}</span> of{" "}
                    <span className="font-medium">{filteredComplaints.length}</span> complaints
                </p>

                <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-between sm:justify-end">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                    >
                        Previous
                    </button>

                    <span className="text-sm font-medium px-3 py-2 bg-gray-100 rounded-md">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompletedComplaints;

// import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
// import { useEffect, useMemo, useState } from "react";
// import toast from "react-hot-toast";
// import CustomSelect from "../components/common/CustomSelect";
// import { Loading } from "../components/common/Loading";
// import { useAuth } from "../hooks/useAuth";
// import { db } from "../network/firebaseConfig";

// const CompletedComplaints = () => {
//     const { user, loading: userLoading, users, usersLoading } = useAuth();

//     const [complaints, setComplaints] = useState([]);
//     const [filteredComplaints, setFilteredComplaints] = useState([]);
//     const [loading, setLoading] = useState(true);

//     const [filterStatus, setFilterStatus] = useState("all");
//     const [selectedCategory, setSelectedCategory] = useState("");
//     const [currentPage, setCurrentPage] = useState(1);

//     const itemsPerPage = 10;

//     const statusOptions = ["completed", "paused", "in-operation"];
//     const categoryOptions = [
//         "Red signal",
//         "Router setup",
//         "Billing issue",
//         "New connection setup",
//         "New connection survey",
//         "CCTV survey",
//         "CCTV setup",
//         "Ecommerce",
//         "Other",
//     ];

//     const technicians = useMemo(() => {
//         return users.filter((u) => u.role === "technician");
//     }, [users]);

//     // 🔹 Fetch data
//     useEffect(() => {
//         const unsubscribe = onSnapshot(
//             collection(db, "complaints"),
//             (snapshot) => {
//                 let data = snapshot.docs.map((doc) => ({
//                     id: doc.id,
//                     ...doc.data(),
//                 }));

//                 // Only completed & paused
//                 data = data.filter(
//                     (c) => c.status === "completed" || c.status === "paused"
//                 );

//                 // Role-based filtering
//                 if (user?.role === "technician") {
//                     data = data.filter((c) => c.assignedTo === user.uid);
//                 }

//                 // Sort
//                 data.sort(
//                     (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
//                 );

//                 setComplaints(data);
//                 setLoading(false);
//             },
//             (err) => {
//                 console.error(err);
//                 toast.error("Failed to load completed complaints");
//                 setLoading(false);
//             }
//         );

//         return () => unsubscribe();
//     }, [user]);

//     // 🔹 Combined filtering (status + category)
//     useEffect(() => {
//         let filtered = [...complaints];

//         // Status filter
//         if (filterStatus !== "all") {
//             filtered = filtered.filter((c) => c.status === filterStatus);
//         }

//         // Category filter
//         if (selectedCategory) {
//             filtered = filtered.filter(
//                 (c) => c.category === selectedCategory
//             );
//         }

//         setFilteredComplaints(filtered);
//         setCurrentPage(1);
//     }, [complaints, filterStatus, selectedCategory]);

//     // 🔹 Status update
//     const handleStatusChange = async (complaintId, newStatus) => {
//         try {
//             const complaintRef = doc(db, "complaints", complaintId);
//             const complaint = complaints.find((c) => c.id === complaintId);

//             await updateDoc(complaintRef, {
//                 status: newStatus,
//                 activityLogs: [
//                     ...(complaint.activityLogs || []),
//                     {
//                         time: new Date(),
//                         message: `Status changed to ${newStatus}`,
//                         userId: user.uid,
//                     },
//                 ],
//             });

//             toast.success(`Status updated to ${newStatus}`);
//         } catch (err) {
//             console.error(err);
//             toast.error("Failed to update status");
//         }
//     };

//     if (loading || userLoading || usersLoading) return <Loading />;

//     // 🔹 Empty state with clear filters
//     if (!filteredComplaints.length) {
//         return (
//             <div className="flex flex-col items-center justify-center h-screen gap-4">
//                 <h2 className="text-xl font-semibold">
//                     No complaints found
//                 </h2>

//                 {(filterStatus !== "all" || selectedCategory) && (
//                     <button
//                         onClick={() => {
//                             setFilterStatus("all");
//                             setSelectedCategory("");
//                         }}
//                         className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700"
//                     >
//                         Clear Filters
//                     </button>
//                 )}
//             </div>
//         );
//     }

//     // 🔹 Pagination
//     const totalPages = Math.ceil(
//         filteredComplaints.length / itemsPerPage
//     );

//     const paginated = filteredComplaints.slice(
//         (currentPage - 1) * itemsPerPage,
//         currentPage * itemsPerPage
//     );

//     return (
//         <div className="max-w-6xl mx-auto p-6 space-y-6">
//             {/* 🔹 Header */}
//             <div className="flex justify-between items-center">
//                 <h2 className="text-xl font-semibold">
//                     Completed & Paused Complaints
//                 </h2>

//                 <div className="flex items-center gap-3 w-[40%]">
//                     {/* Status Filter */}
//                     <CustomSelect
//                         name="filterStatus"
//                         options={["all", "completed", "paused"]}
//                         value={filterStatus}
//                         onChange={(name, value) =>
//                             setFilterStatus(value)
//                         }
//                     />

//                     {/* Category Filter */}
//                     <CustomSelect
//                         name="category"
//                         options={categoryOptions}
//                         value={selectedCategory}
//                         placeholder="Category"
//                         onChange={(name, value) =>
//                             setSelectedCategory(value)
//                         }
//                     />

//                     {/* Clear Button */}
//                     {(filterStatus !== "all" || selectedCategory) && (
//                         <button
//                             onClick={() => {
//                                 setFilterStatus("all");
//                                 setSelectedCategory("");
//                             }}
//                             className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
//                         >
//                             Clear
//                         </button>
//                     )}
//                 </div>
//             </div>

//             {/* 🔹 Table */}
//             <table className="w-full border-collapse border border-gray-300">
//                 <thead>
//                     <tr className="bg-gray-100 text-sm">
//                         <th className="border p-2">Ticket</th>
//                         <th className="border p-2">Customer</th>
//                         <th className="border p-2">Phone</th>
//                         <th className="border p-2">Department</th>
//                         <th className="border p-2">Assigned To</th>
//                         <th className="border p-2">Category</th>
//                         <th className="border p-2">Resolved At</th>
//                         <th className="border p-2">Comment</th>
//                         {user?.role === "admin" && (
//                             <th className="border p-2">Action</th>
//                         )}
//                     </tr>
//                 </thead>

//                 <tbody>
//                     {paginated.map((c) => (
//                         <tr key={c.id} className="hover:bg-gray-50 text-sm">
//                             <td className="border p-2 text-center">
//                                 {c.ticketNumber}
//                             </td>
//                             <td className="border p-2 text-center">
//                                 {c.customerInfo?.name}
//                             </td>
//                             <td className="border p-2 text-center">
//                                 {c.customerInfo?.phone}
//                             </td>
//                             <td className="border p-2 text-center">
//                                 {c.department}
//                             </td>
//                             <td className="border p-2 text-center">
//                                 {technicians.find(
//                                     (t) => t.uid === c.assignedTo
//                                 )?.name || "Unassigned"}
//                             </td>
//                             <td className="border p-2 text-center capitalize">
//                                 {/* {c.category} */}
//                                 {c.category || "-"}
//                             </td>
//                             <td className="border p-2 text-center">
//                                 {c.resolvedAt
//                                     ? new Date(
//                                         c.resolvedAt.seconds * 1000
//                                     ).toLocaleString()
//                                     : "—"}
//                             </td>
//                             <td className="border p-2 text-center">
//                                 {c.completionComment || "—"}
//                             </td>

//                             {user?.role === "admin" && (
//                                 <td className="border p-2 text-center">
//                                     <CustomSelect
//                                         name="status"
//                                         options={statusOptions}
//                                         value={c.status}
//                                         onChange={(name, value) =>
//                                             handleStatusChange(
//                                                 c.id,
//                                                 value
//                                             )
//                                         }
//                                     />
//                                 </td>
//                             )}
//                         </tr>
//                     ))}
//                 </tbody>
//             </table>

//             {/* 🔹 Pagination */}
//             <div className="flex justify-between items-center pt-4">
//                 <p className="text-sm text-gray-600">
//                     Showing {paginated.length} of{" "}
//                     {filteredComplaints.length} complaints
//                 </p>

//                 <div className="flex gap-2">
//                     <button
//                         disabled={currentPage === 1}
//                         onClick={() =>
//                             setCurrentPage((p) => p - 1)
//                         }
//                         className="px-3 py-1 border rounded disabled:opacity-50"
//                     >
//                         Prev
//                     </button>

//                     <span className="text-sm font-medium">
//                         Page {currentPage} of {totalPages}
//                     </span>

//                     <button
//                         disabled={currentPage === totalPages}
//                         onClick={() =>
//                             setCurrentPage((p) => p + 1)
//                         }
//                         className="px-3 py-1 border rounded disabled:opacity-50"
//                     >
//                         Next
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default CompletedComplaints;